import { 
  PencilIcon,
  TrashIcon,
  ClockIcon,
  CurrencyDollarIcon
} from "@heroicons/react/24/outline";
import { Button } from "@/components";
import type { ProjectResource } from "@/features/Projects/models/project.types";
import { RESOURCE_TYPE_CONFIG, getResourceTypeIcon } from "../resourcesPhase.definitions";

interface ResourcesListProps {
  resources: ProjectResource[];
  onEdit: (resource: ProjectResource) => void;
  onDelete: (resourceId: string) => void;
  showDeleteConfirm: string | null;
  setShowDeleteConfirm: (id: string | null) => void;
}

export function ResourcesList({ 
  resources, 
  onEdit, 
  onDelete, 
  showDeleteConfirm, 
  setShowDeleteConfirm 
}: ResourcesListProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          Recursos da Equipe ({resources.length})
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Membros da equipe e suas estimativas de custo
        </p>
      </div>

      <div className="divide-y divide-gray-200">
        {resources.map((resource) => {
          const IconComponent = getResourceTypeIcon(resource.type);
          const typeConfig = RESOURCE_TYPE_CONFIG[resource.type];
          
          return (
            <div key={resource.id} className="p-6">
              {/* Delete confirmation */}
              {showDeleteConfirm === resource.id && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-800 mb-3">
                    Tem certeza que deseja excluir este recurso?
                  </p>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => onDelete(resource.id)}
                      variant="danger"
                      size="sm"
                    >
                      Confirmar
                    </Button>
                    <Button
                      onClick={() => setShowDeleteConfirm(null)}
                      variant="outline"
                      size="sm"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  {/* Icon */}
                  <div className={`
                    w-12 h-12 rounded-lg flex items-center justify-center
                    ${typeConfig?.color === 'blue' ? 'bg-blue-100' :
                      typeConfig?.color === 'purple' ? 'bg-purple-100' :
                      typeConfig?.color === 'green' ? 'bg-green-100' :
                      typeConfig?.color === 'orange' ? 'bg-orange-100' :
                      typeConfig?.color === 'indigo' ? 'bg-indigo-100' :
                      'bg-gray-100'}
                  `}>
                    <IconComponent className={`
                      w-6 h-6 
                      ${typeConfig?.color === 'blue' ? 'text-blue-600' :
                        typeConfig?.color === 'purple' ? 'text-purple-600' :
                        typeConfig?.color === 'green' ? 'text-green-600' :
                        typeConfig?.color === 'orange' ? 'text-orange-600' :
                        typeConfig?.color === 'indigo' ? 'text-indigo-600' :
                        'text-gray-600'}
                    `} />
                  </div>

                  {/* Details */}
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{resource.name}</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      {typeConfig?.label || resource.type}
                    </p>
                    
                    {/* Stats */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <ClockIcon className="w-4 h-4" />
                        <span>{resource.hoursEstimated}h</span>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <CurrencyDollarIcon className="w-4 h-4" />
                        <span>R$ {resource.hourlyRate}/h</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions and Cost */}
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      R$ {resource.totalCost.toLocaleString('pt-BR')}
                    </div>
                    <div className="text-xs text-gray-500">
                      Custo total
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onEdit(resource)}
                      className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Editar recurso"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => setShowDeleteConfirm(resource.id)}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      title="Excluir recurso"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Description */}
              {typeConfig?.description && (
                <div className="mt-3 pl-16">
                  <p className="text-xs text-gray-500">
                    {typeConfig.description}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}